<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiStandardButton, UiTooltip, UiChip } from "@shopexperts/shared";
import type { AiSummaryStep, ExpertAiSummaryOverview } from "@shopexperts/legacy-api";
import {
  AI_SUMMARY_STEP_DESCRIPTIONS,
  AI_SUMMARY_STEP_LABELS,
  AI_SUMMARY_STEP_ORDER,
  formatAiCost,
  formatAiDateTime,
  formatAiDuration,
  formatAiModel,
  formatAiTokens,
  isAiRunActive,
} from "@/app/composables/expert-manager/aiSummaries";
import AiRunStatusChip from "./AiRunStatusChip.vue";

const props = withDefaults(
  defineProps<{
    overview: ExpertAiSummaryOverview;
    isBusy?: boolean;
    canRegenerate?: boolean;
  }>(),
  { isBusy: false, canRegenerate: true },
);

const emit = defineEmits<{
  (e: "regenerate", step: AiSummaryStep): void;
  (e: "history", step: AiSummaryStep): void;
  (e: "view-run", runId: number): void;
}>();

const rows = computed(() =>
  AI_SUMMARY_STEP_ORDER.map((step, index) => {
    const entry = props.overview.steps[step];
    const run = entry.latest_run;
    return {
      step,
      index: index + 1,
      label: AI_SUMMARY_STEP_LABELS[step],
      description: AI_SUMMARY_STEP_DESCRIPTIONS[step],
      dependencies: entry.dependencies.map((dependency) => AI_SUMMARY_STEP_LABELS[dependency]),
      run,
      active: isAiRunActive(run?.status),
      meta: run
        ? [
            formatAiModel(run.model),
            formatAiTokens(run),
            formatAiCost(run.usage.cost),
            formatAiDuration(run),
          ].filter((value): value is string => Boolean(value))
        : [],
    };
  }),
);
</script>

<template>
  <ol class="flex flex-col divide-y divide-se-gray-200">
    <li v-for="row in rows" :key="row.step" class="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:gap-4">
      <div class="flex shrink-0 items-start gap-3 sm:w-64">
        <span
          class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          :class="row.run?.status === 'succeeded'
            ? 'bg-se-success-light text-se-green'
            : row.run?.status === 'failed'
              ? 'bg-se-warn-light text-se-warn'
              : row.active
                ? 'bg-se-dark text-se-white'
                : 'bg-se-gray-100 text-se-gray-500'"
        >
          <UiIcon v-if="row.run?.status === 'succeeded'" name="check" class="h-3 w-3" />
          <UiIcon v-else-if="row.run?.status === 'failed'" name="close" class="h-3 w-3" />
          <UiIcon v-else-if="row.active" name="refresh-cw" class="h-3 w-3 animate-spin" />
          <span v-else>{{ row.index }}</span>
        </span>
        <div class="flex min-w-0 flex-col gap-0.5">
          <UiText as="p" variant="p-small" class="font-semibold text-se-dark" :text="row.label" />
          <UiText as="p" variant="caption-normal" class="text-se-gray-500" :text="row.description" />
          <UiText
            v-if="row.dependencies.length"
            as="p"
            variant="caption-normal"
            class="text-se-gray-500"
            :text="`Needs: ${row.dependencies.join(', ')}`"
          />
        </div>
      </div>

      <div class="flex min-w-0 flex-1 flex-col gap-1.5">
        <template v-if="row.run">
          <div class="flex flex-wrap items-center gap-2">
            <AiRunStatusChip :status="row.run.status" />
            <UiChip :text="`Revision ${row.run.revision}`" variant="plain" size="xs" rounded="full" />
            <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="formatAiDateTime(row.run.finished_at ?? row.run.created_at)" />
          </div>
          <UiText
            v-if="row.meta.length"
            as="p"
            variant="caption-normal"
            class="text-se-gray-600"
            :text="row.meta.join(' · ')"
          />
          <UiText
            v-if="row.run.error"
            as="p"
            variant="caption-normal"
            class="rounded-md bg-se-warn-light px-2 py-1 text-se-warn"
            :text="row.run.error"
          />
        </template>
        <UiText v-else as="p" variant="caption-normal" class="text-se-gray-500" text="Not run yet" />
      </div>

      <div class="flex shrink-0 items-center gap-1.5">
        <UiTooltip content="Input snapshot, parsed output, and the raw provider response of the latest run">
          <UiStandardButton
            variant="ghost"
            size="sm"
            icon="file-placeholder"
            label="Output"
            :disabled="!row.run"
            @click="row.run && emit('view-run', row.run.id)"
          />
        </UiTooltip>
        <UiTooltip content="Every revision of this step">
          <UiStandardButton variant="ghost" size="sm" icon="clock" label="History" @click="emit('history', row.step)" />
        </UiTooltip>
        <UiTooltip content="Run this step again from the latest usable upstream outputs">
          <UiStandardButton
            variant="outline"
            size="sm"
            icon="refresh-cw"
            label="Regenerate"
            :disabled="isBusy || !canRegenerate"
            @click="emit('regenerate', row.step)"
          />
        </UiTooltip>
      </div>
    </li>
  </ol>
</template>
